<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api, apiUrl, isAbortError } from "../api";
import ArtifactTree from "../components/ArtifactTree.vue";
import ComparePanel from "../components/ComparePanel.vue";
import FragmentFileRow from "../components/FragmentFileRow.vue";
import RemoteArtifactTree from "../components/RemoteArtifactTree.vue";
import VersionPicker from "../components/VersionPicker.vue";
import AvailabilityBadge from "../components/AvailabilityBadge.vue";
import { copyTextToClipboard } from "../clipboard";
import { artifactActionLabel, artifactKindForMode, availabilityStatesForMode, availableArchiveModes, buildArchiveOverview, buildSyncStatusPresentation, domainActionSupport, domainFieldSupport, domainModeLabel, hoyoArtifactCardPresentation, isAvailabilityActionable, preferredArtifactAction, preferredDomainArtifactAction , displayVersionLabel} from "../domain-presentation";
import { gameIcons } from "../game-icons";
import { publisherGroups } from "../game-meta";
import type { ArchiveDomain, ArchiveLead, Artifact, Game, VersionDetail, VersionSummary } from "../types";

const route = useRoute();
const router = useRouter();
const games = ref<Game[]>([]);
const domains = ref<ArchiveDomain[]>([]);
const versions = ref<VersionSummary[]>([]);
const versionsDomainId = ref("");
const artifacts = ref<Artifact[]>([]);
const leads = ref<ArchiveLead[]>([]);
const nextCursor = ref<string | null>(null);
const query = ref(String(route.query.q || ""));
const initialAvailability = String(route.query.availability || "all");
const availabilityFilter = ref<"all" | "available" | "unavailable" | "unknown">(
  ["available", "unavailable", "unknown"].includes(initialAvailability) ? initialAvailability as "available" | "unavailable" | "unknown" : "all",
);
const loading = ref(true);
const loadingMore = ref(false);
const toast = ref("");
const error = ref<Error | null>(null);
const registryError = ref<Error | null>(null);
const scopedNotFound = ref("");
let registryController: AbortController | null = null;
let artifactController: AbortController | null = null;
let artifactRequestId = 0;
const gameId = computed(() => String(route.params.gameId || ""));
const domainId = computed(() => String(route.params.domainId || domains.value[0]?.id || ""));
const selectedVersion = computed(() => String(route.params.version || versions.value[0]?.version || ""));
const mode = computed(() => String(route.params.mode || domains.value.find((item) => item.id === domainId.value)?.capabilities[0] || ""));
const game = computed(() => games.value.find((item) => item.id === gameId.value) || null);
const railGroups = computed(() => publisherGroups(games.value));
const domain = computed(() => domains.value.find((item) => item.id === domainId.value) || null);
const uiModes = computed(() => availableArchiveModes(domains.value));
const visibleModes = computed(() => {
  // 完整包入口必须常驻：按当前版本有无 package 数据过滤会让入口在切换
  // 模块后消失（Android domain 不过滤、PC domain 过滤），用户点不回去。
  // 无数据的版本点进完整包模块显示空列表提示即可。
  return uiModes.value;
});
const selectedSummary = computed(() => versions.value.find((item) => item.version === selectedVersion.value) || null);
const selectedDisplayVersion = computed(() => displayVersionLabel(selectedVersion.value, selectedSummary.value?.attributes));
// Multi-channel Android versions (1.2.0@mihoyo + 1.2.0@mihoyo_8) share one
// picker row; the download area must aggregate artifacts across all channels.
const channelVersions = computed(() => {
  if (!selectedVersion.value) return [] as typeof versions.value;
  const base = displayVersionLabel(selectedVersion.value, selectedSummary.value?.attributes);
  return versions.value.filter(
    (item) => item.version !== selectedVersion.value && displayVersionLabel(item.version, item.attributes) === base,
  );
});
const gamePackageCount = computed(() => artifacts.value.filter((item) => item.attributes?.component !== "voice").length);
const compareBaseOptions = computed(() => versions.value.filter((item) => item.version !== selectedVersion.value));
const compareBaseVersion = computed(() => {
  const requested = String(route.query.from || "");
  if (requested && requested !== selectedVersion.value && versions.value.some((item) => item.version === requested)) return requested;
  const currentIndex = versions.value.findIndex((item) => item.version === selectedVersion.value);
  return versions.value[currentIndex + 1]?.version || compareBaseOptions.value[0]?.version || "";
});
const SEARCHABLE_MODES = new Set(["apk", "chunks", "files", "packages", "patches"]);
const searchableMode = computed(() => SEARCHABLE_MODES.has(mode.value));
const isFileTreeMode = computed(() => mode.value === "files" && ["patchersdk", "wuwa", "hoyo"].includes(domain.value?.adapter || ""));
const usesArtifactTree = computed(() => isFileTreeMode.value && Boolean(query.value.trim()));
const usesRemoteTree = computed(() => mode.value === "resources" || (isFileTreeMode.value && !query.value.trim()));
const exportArtifactKind = computed(() => artifactKindForMode(mode.value));
const supportsArtifactField = (field: string) => domainFieldSupport(domain.value, "artifact_fields", field) === "supported";
const supportsDomainAction = (action: string) => domainActionSupport(domain.value, action);
const canExportArtifacts = computed(() => mode.value !== "files" && Boolean(exportArtifactKind.value));
const canExportUrls = computed(() => canExportArtifacts.value && supportsArtifactField("urls") && supportsDomainAction("download"));
const canFilterAvailability = computed(() => !["files", "legacy", "archive", "compare", "manifest"].includes(mode.value) && supportsArtifactField("availability"));
const availabilityStateForRequest = computed(() => mode.value === "files" || availabilityFilter.value === "all" ? undefined : availabilityFilter.value);
const usesPreferredUrlPresentation = computed(() =>
  supportsArtifactField("urls")
  && supportsArtifactField("availability")
  && Boolean(domain.value?.capability_contract?.url_source_kinds?.includes("mirror")),
);
const availabilityFilters = computed(() => {
  const kind = exportArtifactKind.value;
  const kindSummary = kind ? selectedSummary.value?.artifact_kinds?.[kind] : null;
  const states = availabilityStatesForMode(selectedSummary.value, mode.value);
  return [
    { id: "all" as const, label: "全部", count: kind ? kindSummary?.count ?? 0 : selectedSummary.value?.artifact_count || 0 },
    { id: "available" as const, label: "可用", count: states.available || 0 },
    { id: "unavailable" as const, label: "失效", count: states.unavailable || 0 },
    { id: "unknown" as const, label: "未判定", count: states.unknown || 0 },
  ];
});
const availabilityEvidenceNote = computed(() => (
  "“可用”只表示受限 Range 请求收到有效内容，不保证完整文件传输或所有下载器兼容。"
));
const fileWorkspaceSubtitle = "列表 / 对比 / Chunk";
const panelEyebrow = computed(() => {
  if (mode.value === "resources") return gameId.value === "endfield" ? "Endfield Windows resources" : "Aether Gazer resources";
  if (domain.value?.adapter === "patchersdk" && mode.value === "files") return `${gameId.value.toUpperCase()} files`;
  if (domain.value?.adapter === "arknights" && mode.value === "packages") return "Arknights packages";
  if (mode.value === "legacy") return "Historical candidates";
  if (domain.value?.adapter === "hoyo") return `HoYo ${modeLabel(domain.value, mode.value)}`;
  if (domain.value?.adapter === "wuwa") return "Wuwa files";
  return modeLabel(domain.value, mode.value);
});
const panelTitle = computed(() => {
  if (mode.value === "compare") return `${selectedVersion.value} 版本对比`;
  if (mode.value === "manifest") return "官方清单文件";
  if (mode.value === "archive") return `${selectedVersion.value} 归档信息`;
  if (mode.value === "legacy") return "候选线索";
  return `${selectedDisplayVersion.value} ${modeLabel(domain.value, mode.value)}`;
});
const displayGameName = computed(() => {
  const current = game.value;
  if (!current) return gameId.value;
  return /[\u3400-\u9fff]/.test(current.name) ? current.name : current.sub_name || current.name;
});
const displayGameSubtitle = computed(() => {
  const current = game.value;
  if (!current) return "";
  return displayGameName.value === current.name ? current.sub_name : current.name;
});
const preferredDomain = (items: ArchiveDomain[]): ArchiveDomain | undefined =>
  [...items].sort((left, right) => {
    const score = (item: ArchiveDomain) => item.adapter === "android" || item.capabilities.every((capability) => capability === "apk") ? 10 : 0;
    return score(left) - score(right);
  })[0];
const chunkSummary = computed(() => {
  const rows = artifacts.value.filter((item) => item.kind === "chunk");
  const numeric = (item: Artifact, key: string) => Number(item.attributes?.[key] || 0);
  return {
    buildId: String(rows[0]?.attributes?.build_id || "—"),
    manifests: selectedSummary.value?.artifact_kinds?.chunk?.count || rows.length,
    files: rows.reduce((sum, item) => sum + numeric(item, "file_count"), 0),
    chunks: rows.reduce((sum, item) => sum + numeric(item, "chunk_count"), 0),
    compressed: rows.reduce((sum, item) => sum + item.size, 0),
    uncompressed: rows.reduce((sum, item) => sum + numeric(item, "uncompressed_size"), 0),
  };
});
const kindSummary = (kind: string) => selectedSummary.value?.artifact_kinds?.[kind] || { count: 0, size: 0 };
const archiveOverview = computed(() => buildArchiveOverview({
  domain: domain.value,
  summary: selectedSummary.value,
  mode: mode.value,
  version: selectedVersion.value,
  displayVersion: selectedDisplayVersion.value,
  channelSummaries: channelVersions.value,
  formatBytes,
  formatDate: formatObservedDate,
}));

const adapterSourceLabel = (adapter: string | undefined): string => {
  switch (adapter) {
    case "arknights": return "明日方舟 PC 官方启动器 API";
    case "endfield": return "终末地资源接口 / 上游归档";
    case "wuwa": return "鸣潮官方启动器索引";
    case "hoyo": return "HoYo 官方版本清单";
    case "patchersdk": return "PatcherSDK ResList";
    case "nte": return "异环官方启动器 ResList";
    case "android": return "官方 Android APK 直链";
    case "resources": return "官方资源清单";
    default: return "官方 CDN 清单";
  }
};

const syncStatus = computed(() => {
  const latest = versions.value[0] || null;
  const presentation = buildSyncStatusPresentation({
    domains: domains.value,
    currentDomain: domain.value,
    currentLatest: latest,
  });
  const primarySummary = presentation.primaryDomain?.id === domain.value?.id ? latest : null;
  const observed = primarySummary?.observed_at || primarySummary?.source_updated_at || null;
  const packages = Number(primarySummary?.artifact_kinds?.package?.count ?? 0);
  const patches = Number(primarySummary?.artifact_kinds?.patch?.count ?? 0);
  return {
    title: `${displayGameName.value} 最新归档`,
    primaryLabel: presentation.primaryLabel,
    primaryLatest: presentation.primaryLatest,
    syncedAt: observed,
    source: adapterSourceLabel(presentation.primaryDomain?.adapter || domain.value?.adapter),
    detail: `${packages.toLocaleString()} 个压缩包 / ${patches.toLocaleString()} 个更新包`,
    androidSecondary: presentation.androidSecondary,
  };
});
const legacyNotice = computed(() => {
  if (mode.value === "legacy") return { title: "未确认候选线索", text: "这里保存用户发现的官方 CDN 历史路径。它们不计入正式版本列表，也不代表当前可下载；页面保留路径时间、当前探测状态与 CDX 证据，方便后续版本考古。" };
  if (mode.value === "files") return { title: "文件清单", text: "文件行展示路径、大小和校验值；展开后可使用官方入口与备用 CDN。散文件 URL 不展示 provider、明文地址或探活状态，也不进行可用性检测。" };
  if (mode.value === "resources") return gameId.value === "endfield"
    ? { title: "Windows 运行时资源", text: "页面保存终末地 Windows 资源接口的解码清单。每个游戏版本只取上游最新一次观测，记录逻辑路径、MD5、大小和官方资源 URL；本站不镜像或重新分发游戏资源。" }
    : { title: "Android 资源清单", text: "页面保存深空之眼 Android 更新时暴露的 .bytes 资源清单。清单为明文 JSON，记录逻辑路径、MD5、大小和官方 hash 对象 URL；本站不镜像 .ys 文件，也不重新分发游戏资源。" };
  if (mode.value === "apk") return { title: "Android APK 直链", text: "页面保存已确认的官方 Android APK CDN URL；同步任务会解析支持的官方最新下载入口，发现新 APK 后记录大小、Last-Modified、ETag 与可用状态。该列表从当前可确认版本开始滚动保存，不代表完整历史。" };
  if (domain.value?.adapter === "patchersdk") return { title: "数据来源", text: `页面保存由${displayGameName.value}官方启动器 CDN 清单解析出的 URL、校验信息与下载索引；该分发链路使用 PatcherSDK ResList 结构。` };
  if (domain.value?.adapter === "arknights") return { title: "数据来源", text: "页面读取明日方舟 PC 官方启动器 API，保存完整分卷 URL、MD5、大小与历史版本；新版本不会删除旧版本记录。" };
  if (domain.value?.adapter === "wuwa") return { title: "数据来源", text: "页面读取鸣潮官方启动器索引与 resource index，保存官方 CDN 文件 URL、MD5、多 CDN 候选与目录结构。" };
  if (domain.value?.adapter === "hoyo") {
    const chunkOnly = kindSummary("chunk").count > 0 && !kindSummary("package").count && !kindSummary("file").count;
    return { title: chunkOnly ? "Chunk 独占" : kindSummary("chunk").count ? "Chunk 分发" : "完整包分发", text: chunkOnly
      ? "该 Canary 只保存 legacy 已归档的 Chunk Manifest 元数据、checksum、大小与 provider；不联网展开文件清单，也不执行 live probe。"
      : "页面按当前游戏 capability 展示 legacy 已归档的完整包、差分包、语音语言和 Chunk Manifest；所有状态均来自 API evidence，不执行 live probe。" };
  }
  return { title: "数据来源", text: "页面展示官方 CDN 元数据、URL、校验值及历史可用性；本站不托管或重新分发游戏文件。" };
});
const sourceLinks = computed(() => {
  if (!domainId.value || !selectedVersion.value) return [];
  const basePath = `/domains/${encodeURIComponent(domainId.value)}/versions/${encodeURIComponent(selectedVersion.value)}`;
  const base = apiUrl(basePath);
  if (mode.value === "legacy") return [
    { label: "候选 JSON", href: apiUrl(`/domains/${encodeURIComponent(domainId.value)}/leads`) },
    { label: "本站仓库", href: "https://github.com/kuaichu/Game-Manifest-Index" },
  ];
  if (mode.value === "files") return [];
  if (mode.value === "resources") return [
    { label: "资源版本索引", href: apiUrl(`/domains/${encodeURIComponent(domainId.value)}/versions`) },
    { label: "当前版本 JSON", href: base },
    { label: gameId.value === "endfield" ? "上游资源归档" : "官方资源 CDN", href: gameId.value === "endfield" ? "https://ak-endfield-api-archive.daydreamer-json.cc/" : "https://download-eo.ys4fun.com/android/resources" },
  ];
  if (mode.value === "apk") return [{ label: "Android APK 索引", href: `${base}/artifacts?kind=apk` }, { label: "本站仓库 README", href: "https://github.com/kuaichu/Game-Manifest-Index" }];
  if (domain.value?.adapter === "patchersdk") return [{ label: "官方 ResList 清单", href: `${base}/artifacts?kind=manifest` }, { label: "当前版本 API", href: base }];
  if (domain.value?.adapter === "arknights") return [
    { label: "官方 get_latest API", href: "https://launcher.hypergryph.com/api/game/get_latest?appcode=GzD1CpaWgmSq1wew&launcher_appcode=abYeZZ16BPluCFyT&channel=1&sub_channel=1&launcher_sub_channel=1" },
    { label: "明日方舟 PC 官网", href: "https://ak.hypergryph.com/pcs" },
    { label: "本站方舟 PC 索引", href: base },
  ];
  if (domain.value?.adapter === "wuwa") return [
    { label: "yuhkix/wuwa-downloader", href: "https://github.com/yuhkix/wuwa-downloader" },
    { label: "官方 launcher index", href: base },
    { label: "resource index", href: `${base}/artifacts?kind=file` },
  ];
  if (domain.value?.adapter === "hoyo") return [
    { label: "hoyo-files.amarea.cn", href: "https://hoyo-files.amarea.cn/" },
    { label: "pkg_version 文件清单", href: `${base}/artifacts?kind=file` },
    { label: gameId.value === "hk4e" ? "项目仓库" : "当前版本 API", href: gameId.value === "hk4e" ? "https://github.com/kuaichu/Game-Manifest-Index" : base },
  ];
  return [{ label: "当前版本 API", href: base }, { label: "文件清单 API", href: `${base}/artifacts` }];
});
const legacyLeads = computed(() => [...leads.value].sort((left, right) => String(left.generated_at || "").localeCompare(String(right.generated_at || ""))));
function legacyArchiveUrl(lead: ArchiveLead): string | undefined {
  const url = lead.urls[0];
  const timestamp = String(url?.archive_facts.timestamp || "");
  return timestamp && url?.url ? `https://web.archive.org/web/${timestamp}/${url.url}` : undefined;
}

async function loadRegistry(): Promise<void> {
  registryController?.abort();
  const request = new AbortController();
  registryController = request;
  loading.value = true;
  registryError.value = null;
  scopedNotFound.value = "";
  try {
    const loadedGames = await api.games(request.signal);
    games.value = loadedGames;
    if (!loadedGames.length) {
      domains.value = [];
      versions.value = [];
      return;
    }
    const requestedGame = String(route.params.gameId || "");
    if (requestedGame && !loadedGames.some((item) => item.id === requestedGame)) {
      domains.value = [];
      versions.value = [];
      scopedNotFound.value = `游戏 ${requestedGame} 不存在。`;
      return;
    }
    const targetGame = requestedGame || loadedGames[0]?.id;
    if (!targetGame) return;
    const loadedDomains = [...await api.domains(targetGame, request.signal)].sort((left, right) => {
      const score = (item: ArchiveDomain) => item.adapter === "android" || item.capabilities.every((capability) => capability === "apk") ? 10 : 0;
      return score(left) - score(right);
    });
    domains.value = loadedDomains;
    const requestedDomain = String(route.params.domainId || "");
    if (requestedDomain && !loadedDomains.some((item) => item.id === requestedDomain)) {
      versions.value = [];
      scopedNotFound.value = `归档域 ${requestedDomain} 不属于 ${targetGame}。`;
      return;
    }
    const targetDomain = requestedDomain || preferredDomain(loadedDomains)?.id;
    if (!targetDomain) return;
    const loadedVersions = await api.versions(targetDomain, request.signal);
    versions.value = loadedVersions;
    versionsDomainId.value = targetDomain;
    const requestedVersion = String(route.params.version || "");
    if (requestedVersion && !loadedVersions.some((item) => item.version === requestedVersion)) {
      scopedNotFound.value = `版本 ${requestedVersion} 不存在于 ${targetDomain}。`;
      return;
    }
    const targetVersion = requestedVersion || loadedVersions[0]?.version;
    if (!targetVersion) return;
    const requestedMode = String(route.params.mode || "");
    const targetDomainRow = loadedDomains.find((item) => item.id === targetDomain);
    if (requestedMode && !targetDomainRow?.capabilities.includes(requestedMode)) {
      scopedNotFound.value = `视图 ${requestedMode} 不受 ${targetDomain} 支持。`;
      return;
    }
    const targetMode = requestedMode || targetDomainRow?.capabilities[0];
    if (!targetMode) return;
    const requestedCompareFrom = String(route.query.from || "");
    const targetIndex = loadedVersions.findIndex((item) => item.version === targetVersion);
    const fallbackCompareFrom = loadedVersions[targetIndex + 1]?.version
      || loadedVersions.find((item) => item.version !== targetVersion)?.version
      || "";
    const cleanQuery: Record<string, string> = {};
    const cleanSearch = String(route.query.q || "").trim();
    const cleanAvailability = String(route.query.availability || "");
    if (SEARCHABLE_MODES.has(targetMode) && cleanSearch) cleanQuery.q = cleanSearch;
    if (
      !["files", "legacy", "archive", "compare", "manifest"].includes(targetMode)
      && targetDomainRow?.capability_contract?.artifact_fields?.availability === "supported"
      && ["available", "unavailable", "unknown"].includes(cleanAvailability)
    ) cleanQuery.availability = cleanAvailability;
    const validCompareFrom = requestedCompareFrom !== targetVersion
      && loadedVersions.some((item) => item.version === requestedCompareFrom)
      ? requestedCompareFrom
      : fallbackCompareFrom;
    if (targetMode === "compare" && validCompareFrom) cleanQuery.from = validCompareFrom;
    const queryChanged = JSON.stringify(route.query) !== JSON.stringify(cleanQuery);
    if (!requestedGame || !requestedDomain || !requestedVersion || !requestedMode || queryChanged) {
      await router.replace({
        name: "archive",
        params: { gameId: targetGame, domainId: targetDomain, version: targetVersion, mode: targetMode },
        query: cleanQuery,
      });
    }
    await loadArtifacts(false);
  } catch (reason) {
    if (isAbortError(reason)) return;
    registryError.value = reason instanceof Error ? reason : new Error(String(reason));
  } finally {
    if (registryController === request) loading.value = false;
  }
}

async function navigate(params: Record<string, string>): Promise<void> {
  const nextParams = { ...route.params, ...params };
  const targetMode = String(nextParams.mode || mode.value);
  const targetDomain = String(nextParams.domainId || domainId.value);
  const targetVersion = String(nextParams.version || selectedVersion.value);
  const nextQuery: Record<string, string> = {};
  if (SEARCHABLE_MODES.has(targetMode) && String(route.query.q || "").trim()) {
    nextQuery.q = String(route.query.q).trim();
  }
  if (
    !["files", "legacy", "archive", "compare", "manifest"].includes(targetMode)
    && String(route.query.availability || "")
  ) nextQuery.availability = String(route.query.availability);
  const requestedFrom = String(route.query.from || "");
  if (
    targetMode === "compare"
    && targetDomain === domainId.value
    && requestedFrom
    && requestedFrom !== targetVersion
  ) nextQuery.from = requestedFrom;
  await router.push({ name: "archive", params: nextParams, query: nextQuery });
}

async function navigateMode(targetDomain: ArchiveDomain, targetMode: string): Promise<void> {
  await navigate({ domainId: targetDomain.id, version: targetDomain.id === domainId.value ? selectedVersion.value : targetDomain.latest_version || "", mode: targetMode });
}

function saveTextFile(filename: string, content: string, type: string): void {
  const href = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(href), 0);
}

async function exportArtifacts(format: "urls" | "json"): Promise<void> {
  if (!canExportArtifacts.value || (format === "urls" && !canExportUrls.value)) return;
  const exportController = new AbortController();
  try {
    const rows = await api.allArtifacts(domainId.value, selectedVersion.value, {
      kind: exportArtifactKind.value,
      query: searchableMode.value ? query.value.trim() : "",
    }, exportController.signal);
    const stem = `${gameId.value}-${selectedVersion.value}-${mode.value}`;
    if (format === "json") {
      saveTextFile(`${stem}.json`, `${JSON.stringify(rows, null, 2)}\n`, "application/json;charset=utf-8");
    } else {
      const content = rows
        .map((item) => preferredDomainArtifactAction(domain.value, item, "download")?.url)
        .filter((url): url is string => Boolean(url))
        .join("\n");
      saveTextFile(`${stem}.urls.txt`, `${content}\n`, "text/plain;charset=utf-8");
    }
    toast.value = `已生成 ${rows.length} 项${format === "json" ? " JSON" : " URL 列表"}`;
  } catch (reason) {
    toast.value = reason instanceof Error ? reason.message : "生成导出文件失败";
  } finally {
    window.setTimeout(() => { toast.value = ""; }, 2400);
  }
}

async function loadArtifacts(append: boolean): Promise<void> {
  if (!domainId.value || !selectedVersion.value) return;
  const resolvedDomain = domains.value.find((item) => item.id === domainId.value);
  if (
    resolvedDomain?.game_id !== gameId.value
    || versionsDomainId.value !== domainId.value
    || !versions.value.some((item) => item.version === selectedVersion.value)
  ) return;
  const requestId = ++artifactRequestId;
  artifactController?.abort();
  artifactController = new AbortController();
  append ? loadingMore.value = true : loading.value = true;
  error.value = null;
  try {
    if (mode.value === "legacy") {
      artifacts.value = [];
      nextCursor.value = null;
      leads.value = await api.leads(domainId.value, artifactController.signal);
      return;
    }
    leads.value = [];
    if (mode.value === "compare") {
      artifacts.value = [];
      nextCursor.value = null;
      return;
    }
    if (usesRemoteTree.value) {
      artifacts.value = [];
      nextCursor.value = null;
      return;
    }
    if (usesArtifactTree.value) {
      artifacts.value = await api.allArtifacts(domainId.value, selectedVersion.value, {
        kind: "file", query: query.value.trim(), state: availabilityStateForRequest.value,
      }, artifactController.signal);
      nextCursor.value = null;
      return;
    }
    const baseVersion = selectedVersion.value;
    const channelTargets = channelVersions.value.length
      ? [baseVersion, ...channelVersions.value.map((item) => item.version)]
      : [baseVersion];
    if (channelTargets.length > 1) {
      // Aggregated multi-channel download area: load every channel identity's
      // artifacts in one shot (APK channel sets are small).
      const loaded: Artifact[] = [];
      for (const version of channelTargets) {
        if (artifactController.signal.aborted) return;
        const page = await api.artifacts(domainId.value, version, {
          query: searchableMode.value ? query.value.trim() : "",
          state: availabilityStateForRequest.value,
          kind: artifactKindForMode(mode.value),
          limit: 500,
        }, artifactController.signal);
        loaded.push(...page.items);
      }
      // The aggregated base version (1.2.0) and its channel identities
      // (1.2.0@mihoyo / 1.2.0@mihoyo_8) can point at the same files; dedupe
      // by artifact name so each APK appears exactly once.
      const seenArtifacts = new Set<string>();
      artifacts.value = loaded.filter((item) => {
        const key = String(item.name);
        if (seenArtifacts.has(key)) return false;
        seenArtifacts.add(key);
        return true;
      });
      nextCursor.value = null;
      return;
    }
    const page = await api.artifacts(domainId.value, selectedVersion.value, {
      cursor: append ? nextCursor.value : null,
      query: searchableMode.value ? query.value.trim() : "",
      state: availabilityStateForRequest.value,
      kind: artifactKindForMode(mode.value),
      limit: 50,
    }, artifactController.signal);
    artifacts.value = append ? [...artifacts.value, ...page.items] : page.items;
    nextCursor.value = page.next_cursor;
  } catch (reason) {
    if (isAbortError(reason)) return;
    error.value = reason instanceof Error ? reason : new Error(String(reason));
  } finally {
    if (requestId === artifactRequestId) {
      loading.value = false;
      loadingMore.value = false;
    }
  }
}

async function replaceQuery(name: "q" | "availability" | "from", value: string): Promise<void> {
  const next = { ...route.query };
  if (value && value !== "all") next[name] = value;
  else delete next[name];
  await router.replace({ name: "archive", params: route.params, query: next });
}

function updateCompareBase(value: string): void { void replaceQuery("from", value); }
function onCompareBaseChange(event: Event): void {
  updateCompareBase((event.target as HTMLSelectElement).value);
}

let searchTimer: number | null = null;
watch(
  () => [
    String(route.params.gameId || ""), String(route.params.domainId || ""),
    String(route.params.version || ""), String(route.params.mode || ""),
  ],
  () => void loadRegistry(),
);
watch(query, () => {
  if (searchTimer !== null) window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(async () => {
    await replaceQuery("q", query.value.trim());
    void loadArtifacts(false);
  }, 180);
});
watch(availabilityFilter, async () => {
  await replaceQuery("availability", availabilityFilter.value);
  void loadArtifacts(false);
});
watch(() => route.query.q, (value) => {
  const next = String(value || "");
  if (query.value !== next) query.value = next;
});
watch(() => route.query.availability, (value) => {
  const next = String(value || "all");
  const valid = ["available", "unavailable", "unknown"].includes(next) ? next : "all";
  if (availabilityFilter.value !== valid) availabilityFilter.value = valid as typeof availabilityFilter.value;
});
watch(displayGameName, (value) => { if (value) document.title = `${value}官方 CDN 文件索引｜Game Manifest Index`; }, { immediate: true });
watch(() => route.fullPath, (value) => {
  if (!value.startsWith("/games/")) return;
  try { localStorage.setItem("game-manifest-index-web-view-v1", value); } catch { /* The archive still works when storage is unavailable. */ }
});
onMounted(loadRegistry);
onBeforeUnmount(() => {
  registryController?.abort();
  artifactController?.abort();
  if (searchTimer !== null) window.clearTimeout(searchTimer);
});

function formatBytes(value: number): string {
  // Keep the legacy site's familiar labels while retaining its binary sizing.
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value, unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return `${size.toFixed(unit ? 2 : 0)} ${units[unit]}`;
}

function shortName(item: Game): string { return item.id.slice(0, 3).toUpperCase(); }
function iconFor(id: string): string | undefined {
  const source = games.value.find((item) => item.id === id)?.icon_source?.trim();
  if (source?.startsWith("builtin:")) return gameIcons[source.slice("builtin:".length)] || gameIcons[id];
  return source || gameIcons[id];
}
function useFallbackIcon(event: Event, id: string): void {
  const image = event.currentTarget as HTMLImageElement;
  const fallback = gameIcons[id];
  if (fallback && image.src !== fallback) image.src = fallback;
  else image.hidden = true;
}
function modeLabel(item: ArchiveDomain | null | undefined, capability: string): string { return domainModeLabel(item, capability); }
function leadClassificationLabel(lead: ArchiveLead): string {
  const value = String(lead.urls[0]?.current_facts.classification || lead.inferred_context || "");
  return ({
    complete_archived: "完整归档",
    partial_archived: "部分归档",
    missing_list: "清单缺失",
    malformed_list: "清单异常",
    historical_404: "历史 404",
    unresolved: "待确认",
  } as Record<string, string>)[value] || `${lead.platform || "未知平台"} 候选`;
}
function leadActionAllowed(lead: ArchiveLead, action: "open" | "copy"): boolean {
  return domainActionSupport(domain.value, action) && lead.urls[0]?.current_facts.action_allowed === true;
}
function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  const parts = new Intl.DateTimeFormat("zh-CN", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date).reduce<Record<string, string>>((result, part) => {
    if (part.type !== "literal") result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}.${parts.month}.${parts.day} ${parts.hour}:${parts.minute}`;
}
function formatRelativeTime(value?: string | null): string {
  const date = observedDate(value);
  if (!date) return "-";
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "刚刚";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} 天前`;
  return formatDate(value);
}
function observedDate(value?: string | null): Date | null {
  if (!value) return null;
  const normalized = /(?:z|[+-]\d\d:\d\d)$/i.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}
function formatObservedDate(value?: string | null): string {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value.replaceAll("-", ".");
  const date = observedDate(value);
  if (!date) return "—";
  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date).reduce<Record<string, string>>((result, part) => {
    if (part.type !== "literal") result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}.${parts.month}.${parts.day} ${parts.hour}:${parts.minute}`;
}
async function copyArtifactUrl(artifact: Artifact): Promise<void> {
  const url = preferredDomainArtifactAction(domain.value, artifact, "copy")?.url;
  await copyUrl(url);
}
async function copyUrl(url?: string): Promise<void> {
  if (!url) return;
  toast.value = await copyTextToClipboard(url) ? "链接已复制" : "复制失败，请手动复制";
  window.setTimeout(() => { toast.value = ""; }, 1600);
}
function candidateUrl(artifact: Artifact, sourceKind: string) { return artifact.urls.find((item) => item.source_kind === sourceKind); }
function rawArtifactUrl(artifact: Artifact): string | undefined { return artifact.urls[0]?.url; }
function archiveAvailabilityLabel(artifact: Artifact): string {
  return artifactActionLabel(artifact);
}
function preferredAvailableUrl(artifact: Artifact, action: "open" | "copy" = "open"): string | undefined {
  return preferredDomainArtifactAction(domain.value, artifact, action)?.url;
}
function actionableCandidate(artifact: Artifact, sourceKind: string) {
  const candidate = candidateUrl(artifact, sourceKind);
  return domainActionSupport(domain.value, "open") && candidate && isAvailabilityActionable(candidate.current, candidate.url) ? candidate : undefined;
}
function patchRouteText(artifact: Artifact): string {
  const from = String(artifact.attributes.route_from || "");
  const to = String(artifact.attributes.route_to || "");
  return from && to ? `${from} -> ${to}` : String(artifact.attributes.route || "更新路线未知");
}
function packageLabel(artifact?: Artifact): string {
  if (domain.value?.adapter === "arknights") return "官方完整分卷";
  if (domain.value?.adapter === "hoyo" && artifact) return hoyoArtifactCardPresentation(artifact, selectedVersion.value).label;
  return "官方完整包";
}
function packageSource(): string {
  if (domain.value?.adapter === "arknights") return "Hypergryph launcher API";
  if (domain.value?.adapter === "hoyo") return "HoYo launcher API";
  return "official launcher API";
}
function availabilityText(artifact: Artifact): string {
  return artifactActionLabel(artifact);
}

function apkSourceSize(artifact: Artifact): number | null {
  const value = artifact.attributes.source_size;
  return typeof value === "number" && value > 0 ? value : null;
}
function apkSourceSizeText(artifact: Artifact): string {
  const value = apkSourceSize(artifact);
  return value === null ? "" : formatBytes(value);
}

function packageSubtitle(artifact: Artifact, index: number): string {
  if (domain.value?.adapter === "hoyo") return hoyoArtifactCardPresentation(
    artifact, selectedVersion.value, index, gamePackageCount.value || artifacts.value.length,
  ).subtitle;
  return `分卷 ${artifact.part || index + 1} / ${packageSource()} / ${availabilityText(artifact)}`;
}

function hoyoPatchLabel(artifact: Artifact): string { return hoyoArtifactCardPresentation(artifact, selectedVersion.value).label; }
function hoyoPatchSubtitle(artifact: Artifact): string {
  return hoyoArtifactCardPresentation(artifact, selectedVersion.value).subtitle;
}
function patchRouteLabel(name: string): string { return name.replace("→", "->"); }
function patchBasePath(artifact: Artifact): string {
  const raw = artifact.urls[0]?.url;
  if (!raw) return "—";
  try {
    const path = decodeURIComponent(new URL(raw, window.location.origin).pathname).replace(/^\/+/, "");
    return path.replace(/indexFile\.json$/i, "resources/");
  } catch { return raw; }
}
function chunkManifestId(artifact: Artifact): string {
  const stored = artifact.attributes?.manifest_id;
  if (stored) return String(stored);
  const raw = artifact.urls[0]?.url;
  if (!raw) return "—";
  try { return decodeURIComponent(new URL(raw).pathname.split("/").filter(Boolean).at(-1) || "—"); }
  catch { return artifact.name; }
}
function chunkMatchField(name: string): string {
  const value = name.toLowerCase();
  if (value.includes("中文") || value.includes("chinese")) return "zh-cn";
  if (value.includes("英文") || value.includes("english")) return "en-us";
  if (value.includes("日文") || value.includes("japanese")) return "ja-jp";
  if (value.includes("韩文") || value.includes("korean")) return "ko-kr";
  return "game";
}
function chunkMatchingField(artifact: Artifact): string {
  return String(artifact.attributes?.matching_field || chunkMatchField(artifact.name));
}
</script>

<template>
  <main v-if="registryError" class="not-found-view error-state"><p class="kicker">Registry Error</p><h1>归档目录加载失败</h1><p>{{ registryError.message }}</p><button class="tool-button" type="button" @click="loadRegistry">重试</button></main>
  <main v-else-if="scopedNotFound" class="not-found-view"><p class="kicker">Scoped Not Found</p><h1>请求的归档范围不存在</h1><p>{{ scopedNotFound }}</p><button class="tool-button" type="button" @click="navigate({ gameId: games[0]?.id || '', domainId: '', version: '', mode: '' })">返回可用归档</button></main>
  <main v-else-if="!loading && !games.length" class="not-found-view"><p class="kicker">Empty Registry</p><h1>还没有游戏归档</h1><p>registry 当前没有公开游戏。</p></main>
  <main v-else-if="!loading && games.length && !domains.length" class="not-found-view"><p class="kicker">Empty Domains</p><h1>这个游戏还没有归档域</h1><p>稍后同步数据后再试。</p></main>
  <main v-else-if="!loading && domains.length && !versions.length" class="not-found-view"><p class="kicker">Empty Versions</p><h1>这个归档域还没有版本</h1><p>当前没有可公开查询的版本。</p></main>
  <div v-else class="archive-app">
    <aside class="game-rail" aria-label="游戏选择">
      <div v-for="group in railGroups" :key="group.publisher" class="rail-group">
        <span class="rail-group-label" :title="group.publisher">{{ group.publisher }}</span>
        <button v-for="item in group.games" :key="item.id" class="game-mark" :class="{ active: item.id === gameId }" :title="`${item.name} · ${item.sub_name}`" @click="navigate({ gameId: item.id, domainId: '', version: '', mode: '' })">
          <img v-if="iconFor(item.id)" :class="{ 'endfield-icon': item.id === 'endfield' }" :src="iconFor(item.id)" :alt="item.name" @error="useFallbackIcon($event, item.id)" /><span v-else>{{ shortName(item) }}</span>
        </button>
      </div>
    </aside>
    <nav class="side-nav">
      <div class="brand-block"><span class="brand-logo"><img v-if="iconFor(gameId)" :class="{ 'endfield-icon': gameId === 'endfield' }" :src="iconFor(gameId)" :alt="displayGameName" @error="useFallbackIcon($event, gameId)" /><b v-else>{{ gameId.slice(0, 1).toUpperCase() }}</b></span><div><strong>{{ displayGameName }}</strong><small>{{ displayGameSubtitle }}</small></div></div>
      <div class="side-section"><span class="side-label">工作区</span><a class="side-link active" href="#home"><span>概览</span><small>版本与统计</small></a><a class="side-link" href="#files"><span>文件检索</span><small>{{ fileWorkspaceSubtitle }}</small></a></div>
      <div class="side-section"><span class="side-label">本页锚点</span><a class="side-link compact" href="#notes"><span>数据来源</span></a></div>
      <div class="side-foot"><span>官方下载索引</span><span>不托管游戏文件</span></div>
    </nav>
    <main class="main-content">
      <header id="home" class="topbar"><div><p class="kicker">Unofficial URL Archive</p><h1>{{ displayGameName || '游戏' }}官方 CDN 文件索引</h1></div></header>
      <section class="control-strip">
        <VersionPicker :versions="versions" :model-value="selectedVersion" :domain="domain" :label-override="mode === 'legacy' ? '候选线索' : undefined" :show-availability="mode !== 'files'" @select="navigate({ version: $event })" />
        <div class="mode-field"><div class="mode-tabs" role="tablist" aria-label="数据视图"><button v-for="item in visibleModes" :key="`${item.domain.id}:${item.capability}`" class="mode-tab" :class="{ active: item.domain.id === domainId && item.capability === mode }" @click="navigateMode(item.domain, item.capability)">{{ modeLabel(item.domain, item.capability) }}</button></div></div>
        <label v-if="searchableMode" class="search-box"><span>搜索</span><input v-model="query" placeholder="文件名 / MD5 / URL" /></label>
      </section>
      <section v-if="syncStatus" class="sync-status" aria-label="同步状态"><div class="sync-current"><div class="sync-current-head"><span class="sync-pulse" aria-hidden="true"></span><div><p class="kicker">Sync Status</p><h2>{{ syncStatus.title }}</h2></div></div><div class="sync-current-grid"><div><span>{{ syncStatus.primaryLabel }}</span><strong>{{ syncStatus.primaryLatest }}</strong></div><div><span>网页同步</span><strong>{{ formatDate(syncStatus.syncedAt) }}</strong><small>{{ formatRelativeTime(syncStatus.syncedAt) }}</small></div><div><span>来源</span><strong>{{ syncStatus.source }}</strong><small>{{ syncStatus.detail }}</small></div><div v-if="syncStatus.androidSecondary"><span>Android 留档</span><strong>{{ syncStatus.androidSecondary }}</strong><small>已记录 APK 直链</small></div></div></div></section>
      <section class="stats" aria-label="归档概览"><div v-for="metric in archiveOverview.overviewMetrics" :key="metric.label" class="stat"><span>{{ metric.label }}</span><strong :title="metric.value">{{ metric.value }}</strong></div></section>
      <section v-if="archiveOverview.moduleDetails.length" class="module-details" aria-label="模块详情">
        <div class="module-details-head"><p class="kicker">Module Details</p><h2>模块详情</h2></div>
        <dl><div v-for="detail in archiveOverview.moduleDetails" :key="detail.label"><dt>{{ detail.label }}</dt><dd :title="detail.value">{{ detail.value }}</dd></div></dl>
      </section>
      <section id="notes" class="notice"><div class="notice-copy"><strong>{{ legacyNotice.title }}</strong><span>{{ legacyNotice.text }}</span></div><div class="source-links"><a v-for="link in sourceLinks" :key="link.label" class="source-link" :href="link.href" target="_blank" rel="noreferrer">{{ link.label }}</a></div></section>
      <section id="files" class="file-panel">
        <div class="panel-head"><div><p class="kicker">{{ panelEyebrow }}</p><h2>{{ panelTitle }}</h2></div><div v-if="canExportArtifacts || mode === 'compare' || mode === 'legacy'" class="panel-tools"><button v-if="canExportArtifacts" class="tool-button" :disabled="!canExportUrls" @click="exportArtifacts('urls')">URL 列表</button><a v-else-if="mode === 'legacy'" class="tool-button" :href="apiUrl(`/domains/${encodeURIComponent(domainId)}/leads`)" target="_blank" rel="noreferrer">JSON</a><button v-else-if="mode !== 'compare'" class="tool-button" @click="exportArtifacts('json')">JSON</button></div><span v-else class="panel-version">{{ selectedVersion }}</span></div>
        <div v-if="canFilterAvailability" class="availability-toolbar" aria-label="URL 探活筛选"><span>URL 探活</span><button v-for="item in availabilityFilters" :key="item.id" :class="{ active: availabilityFilter === item.id }" @click="availabilityFilter = item.id">{{ item.label }} <b>{{ item.count.toLocaleString() }}</b></button><small>{{ availabilityEvidenceNote }}</small></div>
        <div v-if="loading" class="empty">正在读取归档 API…</div>
        <div v-else-if="error" class="empty error-state"><strong>内容加载失败</strong><span>{{ error.message }}</span><button class="tool-button" type="button" @click="loadArtifacts(false)">重试</button></div>
        <div v-else-if="mode === 'legacy' && legacyLeads.length" class="file-list legacy-candidate-list">
          <article v-for="lead in legacyLeads" :key="lead.id" class="file-card legacy-candidate-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 7 8-4 8 4-8 4-8-4Z"/><path d="m4 7 8 4 8-4v10l-8 4-8-4V7Z"/><path d="M12 11v10"/></svg></div>
            <div class="file-main"><div class="file-title"><span class="pill">{{ leadClassificationLabel(lead) }}</span><span class="count">{{ formatDate(lead.generated_at || undefined) }}</span><strong>{{ lead.filename }}</strong></div><div class="file-meta"><span>归档证据 HTTP {{ lead.urls[0]?.current_facts.status_code ?? '未知' }} / {{ lead.urls[0]?.current_facts.reason ?? '无判定' }}</span><span v-if="lead.urls[0]?.archive_facts.classification_reason">{{ lead.urls[0].archive_facts.classification_reason }}</span><span v-if="lead.urls[0]?.archive_facts.digest"># {{ lead.urls[0].archive_facts.digest }}</span></div><div class="file-path">{{ lead.urls[0]?.url }}</div></div>
            <div v-if="leadActionAllowed(lead, 'copy') || leadActionAllowed(lead, 'open')" class="file-actions"><button v-if="leadActionAllowed(lead, 'copy')" class="icon-button" @click="copyUrl(lead.urls[0]?.url)"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3"/></svg>复制链接</button><a v-if="leadActionAllowed(lead, 'open') && lead.urls[0]?.url" class="icon-button" :href="lead.urls[0].url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg>下载</a><a v-if="leadActionAllowed(lead, 'open') && legacyArchiveUrl(lead)" class="icon-button mirror-link" :href="legacyArchiveUrl(lead)" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 5h5v5"/><path d="m10 14 9-9"/><path d="M19 13v6H5V5h6"/></svg>CDX 快照</a></div>
          </article>
        </div>
        <div v-else-if="mode === 'legacy'" class="empty">没有候选线索记录。</div>
        <div v-else-if="mode === 'compare' && compareBaseVersion" class="compare-shell">
          <div class="compare-toolbar"><div><span>对比范围</span><strong>{{ compareBaseVersion }} → {{ selectedVersion }}</strong></div><label><span>基准版本</span><select :value="compareBaseVersion" @change="onCompareBaseChange"><option v-for="item in compareBaseOptions" :key="item.version" :value="item.version">{{ item.version }}</option></select></label><p>差异由服务端按稳定 artifact identity 计算并分页返回；对比响应不包含 URL 或 availability。</p></div>
          <ComparePanel :domain-id="domainId" :from-version="compareBaseVersion" :to-version="selectedVersion" />
        </div>
        <div v-else-if="mode === 'compare'" class="empty">当前版本没有可用的对比基准。</div>
        <div v-else-if="mode === 'manifest' && artifacts.length" class="manifest-table"><div class="manifest-head"><span>路径</span><span>大小</span><span>校验值</span></div><div v-for="artifact in artifacts" :key="artifact.id" class="manifest-row"><strong>{{ artifact.name }}</strong><span>{{ formatBytes(artifact.size) }}</span><code>{{ artifact.checksum_value || '—' }}</code></div><button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button></div>
        <div v-else-if="mode === 'manifest'" class="empty">当前版本没有官方清单记录。</div>
        <template v-if="!loading && !error && !['legacy', 'archive', 'compare', 'manifest'].includes(mode)">
        <div v-if="isFileTreeMode && domain?.adapter === 'patchersdk'" class="notice file-browser-note"><div class="notice-copy"><strong>清单口径</strong><span>这里展示的是官方 ResList 中可直接下载的游戏对象数量，不等同于本地安装目录递归后的文件数；启动器 Allfile 属于 launcher 独立清单。</span></div></div>
        <div v-if="isFileTreeMode && domain?.adapter === 'wuwa'" class="notice file-browser-note"><div class="notice-copy"><strong>文件索引</strong><span>鸣潮官方启动器提供散文件索引；当前版本 {{ selectedVersion }} 含 {{ selectedSummary?.artifact_kinds.file?.count.toLocaleString() || 0 }} 个文件。展开文件后可使用官方入口与备用 CDN；页面不展示逐 URL 明文或探活状态，也不执行可用性检测。</span></div></div>
        <div v-if="usesRemoteTree && domain" class="remote-workspace">
          <template v-if="mode === 'resources' && selectedSummary">
            <div class="chunk-summary resource-summary">
              <div><span>资源版本</span><strong>{{ selectedSummary.attributes.resource_version || selectedVersion }}</strong></div>
              <div><span>文件数</span><strong>{{ selectedSummary.artifact_kinds.resource?.count.toLocaleString() || '0' }}</strong></div>
              <div><span>总大小</span><strong>{{ formatBytes(selectedSummary.artifact_kinds.resource?.size || 0) }}</strong></div>
              <div><span>{{ gameId === 'endfield' ? '平台' : 'Unity' }}</span><strong>{{ gameId === 'endfield' ? 'Windows' : selectedSummary.attributes.unity_version || '2022.3.62f3' }}</strong></div>
            </div>
            <div class="notice file-browser-note"><div class="notice-copy"><strong>资源口径</strong><span>{{ gameId === 'endfield' ? '这里展示终末地 Windows 资源清单解码后的路径、MD5、大小与官方文件 URL；同一游戏版本只保留上游最新一次观测。' : '这里展示的是 Android 更新清单里的逻辑路径、MD5、大小和官方 hash 对象 URL；.ys 内容可按 UnityFS 或 CRI 音频包继续解析，但本站只保存索引和直链。' }}</span></div></div>
          </template>
          <RemoteArtifactTree :domain-id="domain.id" :version="selectedVersion" :kind="mode === 'resources' ? 'resource' : 'file'" :availability-state="availabilityStateForRequest" :allow-actions="supportsDomainAction('open')" />
        </div>
        <div v-else-if="!artifacts.length" class="empty">{{ domain?.adapter === 'hoyo' && mode === 'packages' ? '该版本没有压缩包直链' : '没有符合当前条件的归档记录。' }}</div>
        <ArtifactTree v-else-if="usesArtifactTree" :artifacts="artifacts" />
        <div v-else-if="mode === 'files'" class="remote-workspace fragment-file-list">
          <div class="hoyo-browser"><FragmentFileRow v-for="artifact in artifacts" :key="artifact.id" :artifact="artifact" /></div>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        <div v-else-if="mode === 'apk'" class="file-list apk-list">
          <article v-for="artifact in artifacts" :key="artifact.id" class="file-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg></div>
            <div class="file-main">
              <div class="file-title"><span class="pill">Android APK</span><span class="count">{{ artifact.part || 1 }}/{{ artifacts.length }}</span><strong>{{ artifact.name }}</strong></div>
              <div class="file-meta">
                <span>{{ availabilityText(artifact) }}<template v-if="supportsArtifactField('size') && apkSourceSize(artifact) !== null"> / {{ apkSourceSizeText(artifact) }}</template></span>
                <span v-if="supportsArtifactField('checksum') && artifact.checksum_value">{{ artifact.checksum_type }} {{ artifact.checksum_value }}</span>
                <span v-if="supportsArtifactField('variant') && artifact.attributes.artifact_variant">渠道/变体 {{ artifact.attributes.artifact_variant }}</span>
              </div>
              <div class="file-path">{{ artifact.urls[0]?.provider || artifact.urls[0]?.source_kind || 'unknown' }} / {{ formatObservedDate(artifact.urls[0]?.current?.checked_at) }}</div>
            </div>
            <div class="file-actions"><AvailabilityBadge :value="preferredArtifactAction(artifact)?.current || artifact.urls[0]?.current || null" /><button v-if="preferredAvailableUrl(artifact, 'copy')" class="icon-button" @click="copyArtifactUrl(artifact)"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制链接</span></button><a v-if="actionableCandidate(artifact, 'official')" class="icon-button" :href="actionableCandidate(artifact, 'official')?.url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>下载</span></a><a v-if="actionableCandidate(artifact, 'archive')" class="icon-button mirror-link" :href="actionableCandidate(artifact, 'archive')?.url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>归档路径</span></a><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div>
          </article>
        </div>
        <div v-else-if="mode === 'packages' && usesPreferredUrlPresentation" class="file-list package-list endfield-list">
          <article v-for="(artifact, index) in artifacts" :key="artifact.id" class="file-card package-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg></div>
            <div class="file-main"><div class="file-title"><span class="pill">{{ candidateUrl(artifact, 'mirror') ? '完整分卷' : '官方完整分卷' }}</span><span class="count">{{ artifact.part || index + 1 }}/{{ selectedSummary?.artifact_kinds?.package?.count || artifacts.length }}</span><strong>{{ artifact.name }}</strong></div><div class="file-meta"><span v-if="supportsArtifactField('size')">{{ formatBytes(artifact.size) }}</span><span v-if="supportsArtifactField('checksum') && artifact.checksum_value"># {{ artifact.checksum_value }}</span></div><div class="file-path">{{ archiveAvailabilityLabel(artifact) }} / {{ candidateUrl(artifact, 'mirror') ? '上游归档保留镜像候选；状态来自归档证据' : '状态来自上游归档证据' }}</div></div>
            <div class="file-actions endfield-actions"><AvailabilityBadge :value="artifact.urls.find((item) => item.url === preferredAvailableUrl(artifact))?.current || candidateUrl(artifact, 'official')?.current || null" /><button v-if="preferredAvailableUrl(artifact, 'copy')" class="icon-button" @click="copyUrl(preferredAvailableUrl(artifact, 'copy'))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制链接</span></button><a v-if="actionableCandidate(artifact, 'official')" class="icon-button" :href="actionableCandidate(artifact, 'official')?.url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>下载</span></a><a v-if="actionableCandidate(artifact, 'mirror')" class="icon-button mirror-link" :href="actionableCandidate(artifact, 'mirror')?.url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>镜像</span></a><span v-if="!preferredAvailableUrl(artifact)" class="stale-link">无已验证下载入口</span><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div>
          </article>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        <div v-else-if="mode === 'packages'" class="file-list package-list">
          <article v-for="(artifact, index) in artifacts" :key="artifact.id" class="file-card package-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg></div>
            <div class="file-main"><div class="file-title"><span class="pill">{{ packageLabel(artifact) }}</span><span class="count">{{ index + 1 }}/{{ selectedSummary?.artifact_kinds?.package?.count || artifacts.length }}</span><strong>{{ artifact.name }}</strong></div><div class="file-meta"><span>{{ formatBytes(artifact.size) }}</span><span># {{ artifact.checksum_value || '—' }}</span></div><div class="file-path">{{ packageSubtitle(artifact, index) }}</div></div>
            <div class="file-actions"><AvailabilityBadge :value="preferredArtifactAction(artifact)?.current || artifact.urls[0]?.current || null" /><button v-if="preferredAvailableUrl(artifact, 'copy')" class="icon-button" @click="copyArtifactUrl(artifact)"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制链接</span></button><a v-if="preferredAvailableUrl(artifact)" class="icon-button" :href="preferredAvailableUrl(artifact)" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>打开</span></a><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div>
          </article>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        <div v-else-if="mode === 'patches' && usesPreferredUrlPresentation" class="file-list patch-route-list endfield-list">
          <article v-for="(artifact, index) in artifacts" :key="artifact.id" class="file-card patch-route-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg></div>
            <div class="file-main"><div class="file-title"><span class="pill">更新分卷</span><span class="count">{{ index + 1 }}/{{ selectedSummary?.artifact_kinds?.patch?.count || artifacts.length }}</span><strong>{{ artifact.name }}</strong></div><div class="file-meta"><span v-if="supportsArtifactField('size')">{{ formatBytes(artifact.size) }}</span><span v-if="supportsArtifactField('checksum') && artifact.checksum_value"># {{ artifact.checksum_value }}</span></div><div class="file-path">{{ patchRouteText(artifact) }} / {{ archiveAvailabilityLabel(artifact) }}</div></div>
            <div class="file-actions endfield-actions"><AvailabilityBadge :value="artifact.urls.find((item) => item.url === preferredAvailableUrl(artifact))?.current || candidateUrl(artifact, 'official')?.current || null" /><button v-if="preferredAvailableUrl(artifact, 'copy')" class="icon-button" @click="copyUrl(preferredAvailableUrl(artifact, 'copy'))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制链接</span></button><a v-if="actionableCandidate(artifact, 'official')" class="icon-button" :href="actionableCandidate(artifact, 'official')?.url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>下载</span></a><a v-if="actionableCandidate(artifact, 'mirror')" class="icon-button mirror-link" :href="actionableCandidate(artifact, 'mirror')?.url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>镜像</span></a><span v-if="!preferredAvailableUrl(artifact)" class="stale-link">无已验证下载入口</span><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div>
          </article>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        <div v-else-if="mode === 'patches' && domain?.adapter === 'wuwa'" class="file-list patch-route-list">
          <article v-for="(artifact, index) in artifacts" :key="artifact.id" class="file-card patch-route-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg></div>
            <div class="file-main"><div class="file-title"><span class="pill">更新路线</span><span class="count">{{ index + 1 }}/{{ selectedSummary?.artifact_kinds?.patch?.count || artifacts.length }}</span><strong>{{ patchRouteLabel(artifact.name) }}</strong></div><div class="file-meta"><span>{{ formatBytes(artifact.size) }}</span><span># {{ artifact.checksum_value || '—' }}</span></div><div class="file-path">{{ patchBasePath(artifact) }} / {{ availabilityText(artifact) }}</div></div>
            <div class="file-actions"><AvailabilityBadge :value="preferredArtifactAction(artifact)?.current || artifact.urls[0]?.current || null" /><button v-if="preferredAvailableUrl(artifact, 'copy')" class="icon-button" @click="copyArtifactUrl(artifact)"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制链接</span></button><a v-if="preferredAvailableUrl(artifact)" class="icon-button" :href="preferredAvailableUrl(artifact)" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>打开</span></a><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div>
          </article>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        <div v-else-if="mode === 'patches' && domain?.adapter === 'hoyo'" class="file-list patch-route-list">
          <article v-for="(artifact, index) in artifacts" :key="artifact.id" class="file-card package-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg></div>
            <div class="file-main"><div class="file-title"><span class="pill">{{ hoyoPatchLabel(artifact) }}</span><span class="count">{{ index + 1 }}/{{ selectedSummary?.artifact_kinds?.patch?.count || artifacts.length }}</span><strong>{{ artifact.name }}</strong></div><div class="file-meta"><span>{{ formatBytes(artifact.size) }}</span><span># {{ artifact.checksum_value || '—' }}</span></div><div class="file-path">{{ hoyoPatchSubtitle(artifact) }}</div></div>
            <div class="file-actions"><AvailabilityBadge :value="preferredArtifactAction(artifact)?.current || artifact.urls[0]?.current || null" /><button v-if="preferredAvailableUrl(artifact, 'copy')" class="icon-button" @click="copyArtifactUrl(artifact)"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制链接</span></button><a v-if="preferredAvailableUrl(artifact)" class="icon-button" :href="preferredAvailableUrl(artifact)" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>打开</span></a><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div>
          </article>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        <div v-else-if="mode === 'chunks' && domain?.adapter === 'hoyo'" class="apk-list chunk-list">
          <div class="chunk-summary">
            <div><span>Build Id</span><strong>{{ chunkSummary.buildId }}</strong></div>
            <div><span>Manifest</span><strong>{{ chunkSummary.manifests.toLocaleString() }}</strong></div>
            <div><span>文件数</span><strong>{{ chunkSummary.files.toLocaleString() }}</strong></div>
            <div><span>文件块</span><strong>{{ chunkSummary.chunks.toLocaleString() }}</strong></div>
            <div><span>压缩大小</span><strong>{{ formatBytes(chunkSummary.compressed) }}</strong></div>
            <div><span>解压大小</span><strong>{{ formatBytes(chunkSummary.uncompressed) }}</strong></div>
          </div>
          <article v-for="(artifact, index) in artifacts" :key="artifact.id" class="file-card chunk-card">
            <div class="file-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/></svg></div>
            <div class="file-main">
              <div class="file-title"><span class="pill">Chunk Manifest</span><span class="count">{{ index + 1 }}/{{ selectedSummary?.artifact_kinds?.chunk?.count || artifacts.length }}</span><strong>{{ artifact.name }}</strong></div>
              <div class="file-meta"><span>{{ formatBytes(artifact.size) }}</span><span># {{ artifact.checksum_value || '—' }}</span></div>
              <div class="file-path">manifest id: {{ chunkManifestId(artifact) }} / matching field: {{ chunkMatchingField(artifact) }} / {{ availabilityText(artifact) }}</div>
            </div>
            <div class="file-actions"><AvailabilityBadge :value="preferredArtifactAction(artifact)?.current || artifact.urls[0]?.current || null" /><button v-if="preferredAvailableUrl(artifact, 'copy')" class="icon-button" @click="copyArtifactUrl(artifact)"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制链接</span></button><a v-if="preferredAvailableUrl(artifact)" class="icon-button" :href="preferredAvailableUrl(artifact)" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>下载 Manifest</span></a><span v-else class="stale-link">无可用 Manifest URL</span><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div>
          </article>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        <div v-else class="file-list">
          <article v-for="artifact in artifacts" :key="artifact.id" class="file-card"><span class="file-icon">▧</span><div class="file-body"><div class="file-title"><strong>{{ artifact.name }}</strong><span class="pill">{{ artifact.kind }}</span></div><div class="file-meta"><span>{{ formatBytes(artifact.size) }}</span><span v-if="artifact.checksum_value">{{ artifact.checksum_type }} {{ artifact.checksum_value }}</span><span>{{ artifact.urls.length }} 个 URL</span></div><div class="file-path">{{ artifact.urls[0]?.url }}</div></div><div class="file-actions"><AvailabilityBadge :value="preferredArtifactAction(artifact)?.current || artifact.urls[0]?.current || null" /><a v-if="preferredAvailableUrl(artifact)" class="tool-button" :href="preferredAvailableUrl(artifact)" target="_blank" rel="noreferrer">打开已验证 URL</a><button v-if="!preferredAvailableUrl(artifact) && rawArtifactUrl(artifact)" class="icon-button" @click="copyUrl(rawArtifactUrl(artifact))"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制 URL</span></button></div></article>
          <button v-if="nextCursor" class="load-more" :disabled="loadingMore" @click="loadArtifacts(true)">{{ loadingMore ? '读取中…' : '加载下一页' }}</button>
        </div>
        </template>
      </section>
      <div v-if="toast" class="toast" role="status">{{ toast }}</div>
    </main>
  </div>
</template>
